import { CalendarX, ChevronDown, ChevronUp, X } from 'lucide-react'
import { DRAFT_FILL } from '@/components/dashboard/MonthlyChart'
import { CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { PrivateAmount } from '@/components/ui/PrivateAmount'
import { Tooltip } from '@/components/ui/Tooltip'
import { toNumber } from '@/lib/decimal'
import { formatEuros, formatMonthYear } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { MonthlyRevenuePoint } from '@/lib/report-types'

// Dezelfde afkapping als de verdeel-uitsplitsing op de factuurkaart
// (components/invoices/DistributionSummary.tsx): hetzelfde probleem, dezelfde getallen.
const MAX_ROWS = 5
const SHOWN_WHEN_TRUNCATED = 4

/**
 * Waar een maand uit is opgebouwd: per klant op het persoonlijke dashboard, per persoon
 * op het bedrijfsdashboard. Rendert bewust geen Card maar alleen de inhoud daarvan; de
 * pagina houdt de kaart vast, anders speelt de fade-up-entree bij elke maandwissel
 * opnieuw af.
 */
export function MonthBreakdown({
  point,
  year,
  dimension,
  backLabel,
  onBack,
  expanded,
  onExpandedChange,
}: {
  point: MonthlyRevenuePoint
  year: number
  dimension: 'client' | 'person'
  /** De kaart waar je naar terugkeert; staat in de tooltip en de aria-label van de knop. */
  backLabel: string
  onBack: () => void
  /** Staat buiten dit component, zodat bladeren door de maanden hem niet terugzet. */
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
}) {
  const total = toNumber(point.current)
  const previous = toNumber(point.previous)
  const draftTotal = toNumber(point.draft)
  const rows = point.breakdown

  const truncatable = rows.length > MAX_ROWS
  const collapsed = truncatable && !expanded
  const shown = collapsed ? rows.slice(0, SHOWN_WHEN_TRUNCATED) : rows
  const rest = rows.slice(shown.length)
  const restCount = rest.length
  const restAmount = rest.reduce((sum, row) => sum + toNumber(row.amount), 0)
  const restDraft = rest.reduce((sum, row) => sum + toNumber(row.draft), 0)
  // De staaf toont wat de kolom in de grafiek ook toont: omzet plus concept. Alleen een
  // positief conceptdeel telt in de noemer mee, precies zoals in de grafiek: een negatief
  // bedrag wordt niet getekend en zou de omzetstaaf over de honderd procent duwen.
  const scale = Math.max(1, ...shown.map((row) => toNumber(row.amount) + Math.max(0, toNumber(row.draft))))
  // Bij een enkele regel dragen een staaf van 100% en een totaal dat die regel herhaalt
  // geen enkele vergelijking; dan is de regel zelf het hele verhaal.
  const showBars = rows.length > 1

  return (
    <div className="animate-fade-in">
      <CardHeader
        title={formatMonthYear(point.month, year)}
        subtitle={dimension === 'client' ? 'Per klant' : 'Per persoon'}
        action={
          <Tooltip content={`Terug naar ${backLabel}`} tapToToggle={false}>
            <button type="button" onClick={onBack} aria-label={`Terug naar ${backLabel}`} className="btn-ghost h-8 w-8 shrink-0 p-0 text-ink-faint">
              <X className="h-4 w-4" />
            </button>
          </Tooltip>
        }
      />

      {rows.length === 0 ? (
        <EmptyState icon={CalendarX} title="Geen omzet deze maand" className="mt-4 py-8" />
      ) : (
        <div className="mt-4 space-y-2.5">
          {shown.map((row, i) => (
            <BreakdownRow
              key={row.key}
              label={row.label}
              amount={toNumber(row.amount)}
              draft={toNumber(row.draft)}
              scale={scale}
              index={i}
              showBar={showBars}
            />
          ))}
          {/* Eén knop die van label wisselt, geen twee die elkaar vervangen: dat laatste
              unmount het element onder de focus, waarna een toetsenbordgebruiker weer
              bovenaan de pagina begint en aria-expanded nooit op een blijvend element
              verandert. De rest krijgt bewust geen staaf: die rangschikt de genoemde
              ontvangers onderling, en een opgetelde groep is daar geen deelnemer van. */}
          {truncatable ? (
            <ToggleRow
              label={collapsed ? `Overig (${restCount} ${dimension === 'client' ? 'klanten' : 'personen'})` : 'Minder tonen'}
              amount={collapsed ? restAmount : undefined}
              draft={collapsed ? restDraft : undefined}
              expanded={expanded}
              onClick={() => onExpandedChange(!expanded)}
            />
          ) : null}
        </div>
      )}

      {/* De totalen komen uit de staaf zelf en niet uit een hersommatie van de rijen:
          zouden die ooit uiteenlopen, dan blijft het paneel eerlijk met de grafiek. */}
      {showBars ? (
        <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-hairline-strong pt-3">
          <span className="label-caps">{draftTotal !== 0 ? 'In rekening gebracht' : 'Totaal'}</span>
          <PrivateAmount className="money text-sm font-semibold text-ink">{formatEuros(total)}</PrivateAmount>
        </div>
      ) : null}

      {draftTotal !== 0 ? (
        <p className="mt-1 flex items-baseline justify-between gap-3 text-xs text-ink-soft">
          <span className="inline-flex items-center gap-1.5">
            <span className={cn('h-1.5 w-1.5 rounded-full', DRAFT_FILL)} />
            Nog concept
          </span>
          <PrivateAmount className="money">{formatEuros(draftTotal)}</PrivateAmount>
        </p>
      ) : null}

      {previous > 0 ? (
        <p className="mt-1 flex items-baseline justify-between gap-3 text-xs text-ink-faint">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-(--chart-prev)" />
            {year - 1}
          </span>
          <PrivateAmount className="money">{formatEuros(previous)}</PrivateAmount>
        </p>
      ) : null}
    </div>
  )
}

/**
 * De regel die de afgekapte ontvangers open- en weer dichtklapt. Ingeklapt draagt hij
 * het opgetelde restbedrag, zodat het zichtbare blijft optellen tot de staaf; uitgeklapt
 * doen de regels dat zelf en is er niets meer te tellen.
 */
function ToggleRow({
  label,
  amount,
  draft,
  expanded,
  onClick,
}: {
  label: string
  amount?: number
  draft?: number
  expanded: boolean
  onClick: () => void
}) {
  const Chevron = expanded ? ChevronUp : ChevronDown
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      className="-mx-2 flex w-[calc(100%+1rem)] items-baseline justify-between gap-3 rounded-lg px-2 py-1 text-sm transition-colors hover:bg-fill-1 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40"
    >
      <span className="inline-flex min-w-0 items-baseline gap-1 text-ink-faint">
        <Chevron className="h-3.5 w-3.5 shrink-0 translate-y-0.5" strokeWidth={1.85} />
        <span className="truncate">{label}</span>
      </span>
      {amount === undefined ? null : (
        <span className="flex shrink-0 items-baseline gap-2">
          {draft ? <DraftNote amount={draft} /> : null}
          <PrivateAmount className="money font-medium text-ink" reveal="none">{formatEuros(amount)}</PrivateAmount>
        </span>
      )}
    </button>
  )
}

/**
 * Het conceptdeel van een regel. Geen stip ervoor: het open segment in de staaf eronder
 * wijst het al aan, en de voetregel zet de betekenis er een keer bij.
 */
function DraftNote({ amount }: { amount: number }) {
  return (
    <PrivateAmount className="money shrink-0 text-[11px] leading-none text-ink-faint" reveal="none">
      {amount > 0 ? '+' : ''}
      {formatEuros(amount)}
    </PrivateAmount>
  )
}

/**
 * Zelfde grammatica als PersonBars op het dashboard, alleen compacter, en met het
 * conceptdeel erop gestapeld precies zoals in de maandgrafiek: dicht is in rekening
 * gebracht, open staat nog klaar. Het bedrag rechts blijft dat van de omzet, want dat
 * is het getal waar de totaalregel op sluit.
 */
function BreakdownRow({
  label,
  amount,
  draft,
  scale,
  index,
  showBar,
}: {
  label: string
  amount: number
  draft: number
  scale: number
  index: number
  showBar: boolean
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate text-ink-soft">{label}</span>
        <span className="flex shrink-0 items-baseline gap-2">
          {draft !== 0 ? <DraftNote amount={draft} /> : null}
          <PrivateAmount className="money font-medium text-ink">{formatEuros(amount)}</PrivateAmount>
        </span>
      </div>
      {/* Een creditregel kan negatief zijn, en een negatieve breedte vult de baan juist
          helemaal; dan draagt het bedrag het verhaal alleen. */}
      {(showBar || draft > 0) && amount + draft > 0 ? (
        <div className="mt-1 flex h-1.5 overflow-hidden rounded-full bg-fill-2">
          {amount > 0 ? (
            <div
              className="h-full origin-left animate-grow-bar-x bg-accent-600/80"
              style={{ width: `${(amount / scale) * 100}%`, animationDelay: `${index * 60}ms` }}
            />
          ) : null}
          {draft > 0 ? (
            <div
              className={cn('h-full origin-left animate-grow-bar-x', DRAFT_FILL)}
              style={{ width: `${(draft / scale) * 100}%`, animationDelay: `${index * 60 + 40}ms` }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
